import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Search,
  Filter,
  Package,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  BarChart,
  ChevronLeft,
  ChevronRight,
  Folder
} from 'lucide-react';
import { useAuth, useProject } from '../contexts';
import { Project, ProjectFilter } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../components/LoadingSpinner';
import ProjectFilters from '../components/ProjectFilters';
import { ProjectCard } from '../components/ProjectCard';
import { syncService } from '../services/syncService';
import { offlineStorage } from '../services/offlineStorage';

const ProjectsContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Header = styled.header`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const HeaderSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing[2]};
    
    > * {
      flex: 1;
      min-width: 120px;
    }
  }
`;

const SearchContainer = styled.div`
  position: relative;
  min-width: 300px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: 200px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[10]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sm};
  background: ${({ theme }) => theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${({ theme }) => theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;


const Main = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const StatsRow = styled.div<{ $showStats: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${props => props.$showStats ? 'grid' : 'none'};
  }
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primaryBackground};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[6]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;


const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[12]};
  text-align: center;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 400px;
  margin: 0 auto ${({ theme }) => theme.spacing[6]};
`;


const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[8]};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing[6]} 0;
  text-align: center;
`;

const ErrorIcon = styled(AlertCircle)`
  color: ${({ theme }) => theme.colors.error};
  width: 48px;
  height: 48px;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ErrorDetails = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sm};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const RetryButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.gray300};
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ITEMS_PER_PAGE = 10;

const StatsToggle = styled.button`
  display: none;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const MobileFilters = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 300px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.white};
  z-index: 1000;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  padding: ${({ theme }) => theme.spacing[4]};
`;

const FilterOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;


const LoadingText = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sm};
`;

export const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ProjectFilter>({
    searchTerm: '',
    status: [],
    customerName: '',
    orderId: '',
    projectId: '',
    startDate: null,
    endDate: null,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { user } = useAuth();
  const { setSelectedProject: setProjectContext } = useProject();
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState(true);
  const [showStats, setShowStats] = useState(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);


  const handleProjectClick = async (project: Project) => {
    try {
      setLoading(true);
      // Set the project in context immediately
      setProjectContext(project);
      console.log('ProjectsPage: Project set in context:', project);
      
      // Fetch and cache project details
      const projectDetails = await apiService.getProjectDetailed(project.id);

      // Save to offline storage
      await offlineStorage.saveProjects([projectDetails]);

      // Navigate to project details
      navigate(`/projects/${project.id}`);
    } catch (error) {
      console.error('Error loading project details:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = useCallback(async (forceSync: boolean = false) => {
    try {
      if (initialLoad) {
        setLoading(true);
      }
      setError(null);

      if (forceSync) {
        setSyncing(true);
        await syncService.forceSync();
        setSyncing(false);
      }

      const fetchedProjects = await apiService.getProjects(forceSync);
      setProjects(fetchedProjects);
      // Do not filter here; always filter in handleFilterChange
      setFilteredProjects(fetchedProjects);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [initialLoad]);

  const handleRetry = () => {
    fetchProjects().catch(console.error);
  };

  useEffect(() => {
    if (user) {
      syncService.startPeriodicSync();
      fetchProjects();
    }

    return () => {
      syncService.stopPeriodicSync();
    };
  }, [user, fetchProjects, initialLoad]);

  const getProjectDisplayName = (project: Project) => {
    if (project.so_number) {
      return `${project.code} - SO: ${project.so_number}`;
    }
    return project.code;
  };

  const handleFilterChange = (newFilters: Partial<ProjectFilter>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    setCurrentPage(1); // Reset to first page when filters change

    let filtered = [...projects];

    // Apply status filters
    if (updatedFilters.status.length > 0) {
      filtered = filtered.filter(project => 
        updatedFilters.status.includes(project.status)
      );
    }

    // Apply customer name filter
    if (updatedFilters.customerName) {
      filtered = filtered.filter(project =>
        project.customer_name.toLowerCase().includes(updatedFilters.customerName.toLowerCase())
      );
    }

    // Apply order ID filter
    if (updatedFilters.orderId) {
      filtered = filtered.filter(project =>
        project.order_id.toLowerCase().includes(updatedFilters.orderId.toLowerCase())
      );
    }

    // Apply project ID filter
    if (updatedFilters.projectId) {
      filtered = filtered.filter(project =>
        project.id.toLowerCase().includes(updatedFilters.projectId.toLowerCase())
      );
    }

    // Apply date range filter
    if (updatedFilters.startDate && updatedFilters.endDate) {
      filtered = filtered.filter(project => {
        const projectDate = new Date(project.created_at);
        return projectDate >= updatedFilters.startDate! && 
               projectDate <= updatedFilters.endDate!;
      });
    }

    // Apply search term filter
    if (updatedFilters.searchTerm) {
      const searchTerm = updatedFilters.searchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.id.toLowerCase().includes(searchTerm) ||
        project.so_number?.toLowerCase().includes(searchTerm) ||
        project.customer_name.toLowerCase().includes(searchTerm) ||
        project.order_id.toLowerCase().includes(searchTerm)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[updatedFilters.sortBy as keyof Project];
      const bValue = b[updatedFilters.sortBy as keyof Project];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return updatedFilters.sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (aValue instanceof Date && bValue instanceof Date) {
        return updatedFilters.sortOrder === 'asc'
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }
      
      return 0;
    });

    setFilteredProjects(filtered);
    setTotalPages(Math.ceil(filtered.length / (isMobile ? filtered.length : 99)));
  };


  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading projects...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorContainer>
          <ErrorIcon />
          <ErrorMessage>{error}</ErrorMessage>
          <RetryButton onClick={handleRetry}>
            <RefreshCw size={16} />
            Retry
          </RetryButton>
        </ErrorContainer>
      );
    }

    const startIndex = (currentPage - 1) * (isMobile ? filteredProjects.length : 99);
    const endIndex = isMobile ? filteredProjects.length : startIndex + 99;
    const currentProjects = filteredProjects.slice(startIndex, endIndex);

    const totalCartons = filteredProjects.reduce((sum, p) => {
      const cartons = p.statistics?.cartons?.total_cartons || 0;
      return sum + cartons;
    }, 0);

    return (
      <>
        <StatsRow $showStats={showStats}>
          <StatCard>
            <StatIcon>
              <Folder size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{filteredProjects.length}</StatValue>
              <StatLabel>Total Projects</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard>
            <StatIcon>
              <CheckCircle size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>
                {filteredProjects.filter(p => p.status === 'completed').length}
              </StatValue>
              <StatLabel>Completed</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard>
            <StatIcon>
              <Clock size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>
                {filteredProjects.filter(p => p.status === 'in-progress').length}
              </StatValue>
              <StatLabel>In Progress</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard>
            <StatIcon>
              <Package size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{totalCartons}</StatValue>
              <StatLabel>Total Cartons</StatLabel>
            </StatContent>
          </StatCard>
        </StatsRow>

        {currentProjects.length > 0 ? (
          <ProjectsGrid>
            {currentProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={{
                  ...project,
                  code: project.code || project.id,
                  name: project.name || project.code || project.id,
                  customer_name: project.customer_name || 'No Customer',
                  order_id: project.order_id || 'N/A',
                  order_date: project.order_date || new Date().toISOString(),
                  status: project.status || 'pending',
                  statistics: project.statistics || {
                    cartons: { total_cartons: 0 },
                    panels: { total_panels: 0 },
                    hardware: { total_hardware: 0 }
                  }
                }}
                onClick={() => handleProjectClick(project)}
              />
            ))}
          </ProjectsGrid>
        ) : (
          <EmptyState>
            <Package size={48} />
            <EmptyStateTitle>No Projects Found</EmptyStateTitle>
            <EmptyStateText>
              {filters.searchTerm || filters.status.length > 0
                ? 'Try adjusting your filters or search terms'
                : 'Check with the administrator'}
            </EmptyStateText>            
          </EmptyState>
        )}

        {!isMobile && totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
              Previous
            </PaginationButton>
            <PageInfo>
              Page {currentPage} of {totalPages}
            </PageInfo>
            <PaginationButton
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight size={16} />
            </PaginationButton>
          </PaginationContainer>
        )}
      </>
    );
  };

  return (
    <ProjectsContainer>
      <Header>
        <HeaderContent>
          <HeaderLeft>
            <HeaderTitle>Projects</HeaderTitle>
            <HeaderSubtitle>
              {syncing ? 'Syncing...' : 'Manage your projects'}
            </HeaderSubtitle>
          </HeaderLeft>
          <HeaderActions>
            <SyncButton
              onClick={() => fetchProjects(true)}
              disabled={syncing || !navigator.onLine}
            >
              <RefreshCw
                size={20}
                className={syncing ? 'animate-spin' : ''}
              />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </SyncButton>
            <SearchContainer>
              <SearchIcon size={20} />
              <SearchInput
                type="text"
                placeholder="Search projects..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange({ searchTerm: e.target.value })}
              />
            </SearchContainer>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <FilterButton onClick={() => setIsFiltersOpen(true)}>
                <Filter size={20} />
                Filters
              </FilterButton>
              <StatsToggle onClick={() => setShowStats(!showStats)}>
                <BarChart size={20} />
                {showStats ? 'Hide Stats' : 'Show Stats'}
              </StatsToggle>
            </div>
          </HeaderActions>
        </HeaderContent>
      </Header>

      <Main>
        {renderContent()}
      </Main>

      <ProjectFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        totalProjects={projects.length}
        filteredProjects={filteredProjects.length}
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
      />
    </ProjectsContainer>
  );
};

const SyncButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default ProjectsPage; 